import {
  InlineSelectValue,
  inlineSelectTriggerClasses
} from "@/components/ui/InlineSelectTrigger";
import { formatPrice } from "@/utils/money";
import { Select } from "@medusajs/ui";
import { cn } from "@repo/design-system/utils";
import { Loader2, Truck } from "lucide-react";
import { memo } from "react";

export interface ShippingOption {
  id: string;
  name: string;
  amount?: number | null;
}

export type ShippingMethodSelectVariant = "default" | "inline";

export interface ShippingMethodSelectProps {
  /**
   * Available shipping options to display
   */
  options: ShippingOption[];
  /**
   * Visual variant.
   *
   * - `default`: full-width form control.
   * - `inline`: compact borderless trigger for embedding in a totals row,
   *   where the row already labels it ("Shipping") and the selected price
   *   IS the row's value. The collapsed state reads `Name · $x` - no
   *   `(+$x)` there, because next to a totals column the "+" reads as a
   *   surcharge on top of the row's own number. Options inside the open
   *   dropdown keep the `(+$x)`/`(Free)` format: that is where methods are
   *   compared, so the delta framing is the useful one.
   */
  variant?: ShippingMethodSelectVariant;
  /**
   * Currently selected option ID
   */
  selectedOptionId?: string | null;
  /**
   * The amount the cart actually charges for the selected method
   * (`cart.totals.shipping_total`), shown as the inline row's collapsed value
   * in place of the option's own list price. The option price is what the
   * catalogue says; `shipping_total` is what sums into the total beside it -
   * promotions and threshold reprices apply to the latter only, and a totals
   * row must reconcile with its own column. Options inside the open dropdown
   * keep their list prices: that is where methods are compared. Ignored by
   * the `default` variant; when omitted, inline falls back to the option
   * price.
   */
  displayAmount?: number | null;
  /**
   * Callback when selection changes
   */
  onChange: (optionId: string) => void;
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean;
  /**
   * Whether the component is processing a change
   */
  isPending?: boolean;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field has a validation error
   */
  error?: boolean;
  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

// A null/undefined amount is an unquoted (calculated) price, not a free one -
// claim nothing rather than "(Free)" and let the name stand alone.
const formatShippingPrice = (amount?: number | null) => {
  if (amount == null) return "";
  return amount === 0 ? "(Free)" : `(+${formatPrice(amount)})`;
};

// Collapsed inline trigger: plain value, no parens/plus (see variant docs).
// Here the string IS the totals row's value, so an unquoted price must not
// read as "Free" while the cart total carries a real freight charge.
const formatInlineShippingPrice = (amount?: number | null) => {
  if (amount == null) return "—";
  return amount === 0 ? "Free" : formatPrice(amount);
};

// The collapsed inline label, shared by the single-option readonly branch and
// the dropdown's Select.Value so the two cannot drift.
const inlineLabel = (name: string, amount?: number | null) =>
  `${name} · ${formatInlineShippingPrice(amount)}`;

// The open-dropdown label. Joined rather than interpolated so an unpriced
// option reads "Express", not "Express " with a dangling separator.
const optionLabel = (option: ShippingOption) =>
  [option.name, formatShippingPrice(option.amount)].filter(Boolean).join(" ");

// Inline sits in a totals column, so the price must end flush at the right
// edge to align with the amounts above it: the shared value leads with its
// chevron (the default), keeping the text last.
const inlineTriggerClasses = cn(
  inlineSelectTriggerClasses,
  "!text-xs font-medium !text-gray-700"
);
export const ShippingMethodSelect = memo(function ShippingMethodSelect({
  options,
  variant = "default",
  selectedOptionId,
  displayAmount,
  onChange,
  isLoading = false,
  isPending = false,
  disabled = false,
  error = false,
  ariaLabel = "Select shipping method",
  className
}: ShippingMethodSelectProps) {
  const isInline = variant === "inline";

  // Loading state. Inline deliberately ignores the host `className` here (it
  // targets the *value* - see the dropdown branch): a host re-scaling values
  // to `!text-sm !text-gray-900` must not promote this placeholder into
  // something that reads as a settled price in the totals column.
  if (isLoading) {
    if (isInline) {
      return (
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          Loading...
        </span>
      );
    }
    return (
      <div className={cn("flex items-center gap-2 py-2.5", className)}>
        <Loader2
          className="h-4 w-4 animate-spin text-gray-400"
          aria-hidden="true"
        />
        <span className="text-sm text-gray-400">Loading shipping...</span>
      </div>
    );
  }

  // No options available - same muted, className-free treatment as loading.
  if (options.length === 0) {
    if (isInline) {
      return <span className="text-xs text-gray-500">No shipping options</span>;
    }
    return (
      <div className={cn("py-2.5", className)}>
        <span className="text-sm text-gray-500">No shipping options</span>
      </div>
    );
  }

  const selectedOption = options.find((opt) => opt.id === selectedOptionId);

  // Readonly display when only one option
  if (options.length === 1 && selectedOption) {
    if (isInline) {
      return (
        // `block truncate` so a long method name ellipsizes inside the totals
        // column (the host cell provides min-w-0) instead of widening it.
        <span
          className={cn(
            "block truncate text-xs font-medium text-gray-700",
            className
          )}
        >
          {inlineLabel(
            selectedOption.name,
            displayAmount ?? selectedOption.amount
          )}
        </span>
      );
    }
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 py-2.5 pl-3 pr-4",
          className
        )}
      >
        <Truck className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <span className="text-sm text-gray-700">
          {optionLabel(selectedOption)}
        </span>
      </div>
    );
  }

  // Dropdown when multiple options
  return (
    // Inline routes `className` to the trigger, not the wrapper: it exists so
    // a host row can re-scale the trigger text (e.g. `!text-sm` to match
    // sibling rows - cn is twMerge-backed, so later importants win).
    <div className={cn(isInline ? "min-w-0" : className)}>
      <Select
        // Controlled, with "" (matching no Item) while nothing is selected:
        // `undefined` would let Radix go uncontrolled, so a click on an option
        // whose mutation hasn't round-tripped through the cart yet (or failed)
        // would portal that Item's `(+$x)` text into the collapsed trigger.
        // Controlled, the trigger keeps showing the placeholder until the cart
        // confirms the selection.
        value={selectedOptionId ?? ""}
        onValueChange={onChange}
        disabled={disabled || isPending}
      >
        <Select.Trigger
          aria-label={ariaLabel}
          aria-invalid={error}
          aria-busy={isPending}
          className={isInline ? cn(inlineTriggerClasses, className) : undefined}
        >
          {/* Radix renders Value's children (when present and a value is
              selected) instead of the matching Item's text - that is what
              lets the collapsed inline state drop the `(+$x)` format the
              Items keep. */}
          <Select.Value
            placeholder={
              isInline ? (
                <InlineSelectValue>Select method</InlineSelectValue>
              ) : (
                "Select shipping method"
              )
            }
          >
            {isInline && selectedOption ? (
              <InlineSelectValue>
                {inlineLabel(
                  selectedOption.name,
                  displayAmount ?? selectedOption.amount
                )}
              </InlineSelectValue>
            ) : undefined}
          </Select.Value>
        </Select.Trigger>
        <Select.Content className="z-[100]">
          {options.map((option) => (
            <Select.Item key={option.id} value={option.id}>
              {optionLabel(option)}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    </div>
  );
});
